import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as NafHubLogo } from "assets/images/v2/naf-hub-logo-mobile.svg";
import ProfileMenu from "../profile-menu/ProfileMenu";
import "./GlobalHeader.scss";
import ProfileMenuDropdown from "../profile-menu-dropdown/ProfileMenuDropdown";
import useDesktopChecker from "hooks/useDesktopChecker";
import { ReactComponent as NotificationsIcon } from "assets/images/v2/notification-bell-icon.svg";
import { PalNotificationHandler } from "../notifications";
import { useNotificationContext } from "contexts/NotificationContext";
import useTabletChecker from "hooks/useTabletChecker";
import { useSelector } from "react-redux";
import { getCurrentAccessToken } from "features/authSlice";

interface GlobalHeaderProps {
  onNotificationsClick?: () => void;
  onDocumentNotificationClick?: (documentId: string) => void;
  isNavOpen?: boolean;
  setIsNavOpen?: (open: boolean) => void;
  showLogoOnDesktop?: boolean;
  hideNotifications?: boolean;
}

const GlobalHeader: React.FC<GlobalHeaderProps> = ({ onNotificationsClick, onDocumentNotificationClick, isNavOpen, setIsNavOpen, showLogoOnDesktop = false, hideNotifications = false, }: GlobalHeaderProps) => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isUserAuthenticated = useSelector(getCurrentAccessToken);

  const { notifications, removeNotification, hasNotifications } = useNotificationContext();

  const toggleDropdown = () => {
    setIsProfileMenuOpen((prev) => !prev);
  };

  const handleNotificationsClick = () => {
    if (onNotificationsClick) {
      onNotificationsClick();
      return;
    }

    setIsNotificationsOpen(!isNotificationsOpen);
  };

  const handleNotificationItemClick = async (notificationId: string) => {
    await removeNotification(notificationId);
    setIsNotificationsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  return (
    <div className="global-header">
      {!isDesktop && isTablet && setIsNavOpen && (
        <>
          {!isNavOpen && (
            <button
              className="mobile-nav__hamburger navExpandButton"
              aria-label="Open navigation"
              onClick={() => setIsNavOpen(true)}

            >
              <span>&#9776;</span>
            </button>
          )}
          {isNavOpen && (
            <button
              className="mobile-nav__close navExpandButton"
              aria-label="Close navigation"
              onClick={() => setIsNavOpen(false)}
            >
              <span>&#10005;</span>
            </button>
          )}
        </>
      )}

      {(showLogoOnDesktop || !isDesktop) && (<NafHubLogo className={showLogoOnDesktop && isDesktop ? "global-header__logo__desktop" : "global-header__logo"} title="NAF Hub" />)}

      <div className="global-header__controls">
        {!hideNotifications && (
          <button className="global-header__notification-button" onClick={handleNotificationsClick}>
            <NotificationsIcon />
            {hasNotifications && <span className="new" />}
          </button>
        )}
        {isUserAuthenticated && <ProfileMenu onClick={toggleDropdown} />}
        {(isProfileMenuOpen || isNotificationsOpen) && (
          <div className={`global-header__content ${isNotificationsOpen ? 'notifications' : 'profile'}`} ref={dropdownRef}>
            {isProfileMenuOpen && <ProfileMenuDropdown />}
            {isNotificationsOpen && (
              <div className="global-header__notifications-content">
                <PalNotificationHandler
                  notifications={notifications}
                  onNotificationClick={handleNotificationItemClick}
                  onDocumentNotificationClick={onDocumentNotificationClick}
                  isOpen={isNotificationsOpen}
                  onClose={() => setIsNotificationsOpen(false)}
                  triggerElementSelector=".global-header__notification-button"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalHeader;
